import {
	memo,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react"
import { useQuery } from "@rocicorp/zero/react"
import { useNavigate } from "@tanstack/react-router"
import {
	BubbleMenu,
	EditorContent,
	useEditorState as useTipTapEditorState,
} from "@tiptap/react"
import { AnimatePresence, motion } from "motion/react"
import { IconImageFill18 } from "nucleo-ui-fill-18"

import type { EmailContact } from "@productlane/api"
import { cn } from "@productlane/lib/src/cn"
import { resolveMacroVariables } from "@productlane/lib/src/macro-variables"
import { computeReplyAllRecipients as computeReplyAllRecipientsFor } from "@productlane/lib/src/reply-all-recipients"
import { formatEmailContacts, useTypingEmitter } from "@productlane/ui"
import { INBOUND_SURFACE_DARK_BG } from "@productlane/ui/src/components/slack-style-message-surface"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import type { ReplyType, TabType } from "./types"
import type { SnippetInput } from "@/screens/settings/settings-pages/snippets-settings-page"
import type { Editor } from "@tiptap/react"
import { liveSelectionRect } from "@/components/editor/live-selection-rect"
import { insertSnippet } from "@/components/editor/snippets/insert-snippet"
import { toast } from "@/components/toast"
import { api } from "@/lib/api"
import { useSetBackNavigation } from "@/lib/back-navigation"
import { useIsPhone } from "@/lib/hooks/use-is-phone"
import { useBrowserNotifications } from "@/lib/hooks/useBrowserNotifications"
import { useSession } from "@/lib/session"
import { updateDocState } from "@/lib/sockets/doc-presence"
import { useLiveChatStore } from "@/lib/states/live-chat-state"
import { useThreadLockedBy } from "@/lib/states/thread-lock-state"
import { useZero, useZeroWorkspace } from "@/lib/zero"
import { useThreadSidebarState } from "@/screens/app/sidebar"
import {
	hasUserAuthoredBody,
	isEmptyContentTag,
} from "@/screens/app/threads/helpers"
import { useComposerState } from "@/screens/app/threads/hooks/useComposerState"
import { useEditorState } from "@/screens/app/threads/hooks/useEditorState"
import { useThreadContext } from "../../../thread-context"
import { EmailInputList } from "../../email-input-list"
import { useSidebarAiChatState } from "../../right-bar/ai-chat/sidebar-ai-chat-state"
import { THREAD_COLUMN_PADDING } from "../thread-column"
import {
	resolveAiDraftBadgeState,
	useAiDraftStaleTick,
} from "./ai-draft-badge-state"
import { BAR_MIN_HEIGHT, draggedBarHeight, getBarMaxHeight } from "./bar-resize"
import { BottomBarHeader, FromAddressPicker } from "./bottom-bar-header"
import { CommentToolbar, ReplyToolbar } from "./bottom-bar-toolbars"
import { BubbleMenuFormatButtons } from "./bubble-menu-format-buttons"
import { shouldFocusCommentEditor } from "./comment-focus"
import { ComposeEditorBubbleMenus } from "./compose-editor-bubble-menus"
import { useComposerCollapse } from "./composer-collapse"
import { useComposerDeadZoneFocus } from "./composer-dead-zone"
import { LockedComposer } from "./locked-composer"
import {
	draftSignerFirstName,
	normalizeAiHtml,
	renderAiDraftAsHtml,
} from "./render-ai-draft"
import { replyTypeFor } from "./reply-channel"
import { hasUnuploadedInlineImage } from "./send-guards"
import { useBottomBarShortcuts } from "./use-bottom-bar-shortcuts"
import { useEmailContentPopulation } from "./use-email-content-population"
import { useFromAddress } from "./use-from-address"
import { useResetRecipientsOnForward } from "./use-reset-recipients-on-forward"
import { useSendMessage } from "./use-send-message"

interface Props {
	composeEditor: Editor | null
	composeEditorVisible: boolean
	commentEditor: Editor | null
	onActivateCommentEditor?: () => void
}

const BAR_HEIGHT_STORAGE_KEY = "thread-bottom-bar-height"
// Teammates see "is replying" drop within seconds of a pause, unlike the customer view.
const TEAM_TYPING_IDLE_MS = 5_000

export const BottomBar = memo(
	({
		composeEditor,
		composeEditorVisible,
		commentEditor,
		onActivateCommentEditor,
	}: Props) => {
		const composeEditorState = useTipTapEditorState({
			editor: composeEditor,
			selector: ({ editor }) => ({
				isEmpty: editor?.isEmpty ?? true,
				isFocused: editor?.isFocused ?? false,
				hasUnuploadedImage: hasUnuploadedInlineImage(editor?.state.doc),
			}),
		})
		const commentEditorState = useTipTapEditorState({
			editor: commentEditor,
			selector: ({ editor }) => ({
				isEmpty: editor?.isEmpty ?? true,
				isFocused: editor?.isFocused ?? false,
			}),
		})

		const [activeTab, setActiveTab] = useState<TabType>("reply")

		const [pendingCommentFocus, setPendingCommentFocus] = useState(false)
		useEffect(() => {
			if (
				!shouldFocusCommentEditor({
					pendingFocus: pendingCommentFocus,
					activeTab,
					hasCommentEditor: !!commentEditor,
				})
			) {
				return
			}
			setPendingCommentFocus(false)
			requestAnimationFrame(() => {
				if (commentEditor && !commentEditor.isDestroyed) {
					commentEditor.commands.focus("end", { scrollIntoView: false })
				}
			})
		}, [pendingCommentFocus, activeTab, commentEditor])

		const { thread, setIsComment, setIsReply } = useThreadContext()

		const pendingContentRewrite = useSidebarAiChatState(
			(s) => s.pendingContentRewrite,
		)
		const clearPendingContentRewrite = useSidebarAiChatState(
			(s) => s.setPendingContentRewrite,
		)
		useEffect(() => {
			if (pendingContentRewrite?.target !== "composer" || !composeEditor) return
			setIsReply(true)
			composeEditor
				.chain()
				.setContent(normalizeAiHtml(pendingContentRewrite.html))
				.focus("end", { scrollIntoView: false })
				.run()
			clearPendingContentRewrite(null)
		}, [
			pendingContentRewrite,
			composeEditor,
			setIsReply,
			clearPendingContentRewrite,
		])

		useEffect(() => {
			setActiveTab("reply")
		}, [thread.id])
		const ccInputRef = useRef<HTMLInputElement>(null)
		const bccInputRef = useRef<HTMLInputElement>(null)
		const editorContainerRef = useRef<HTMLDivElement>(null)
		const snippetSuggestionsRef = useRef<{
			openSnippets: () => void
		}>(null)
		const helpArticlesSuggestionsRef = useRef<{
			openHelpArticles: () => void
		}>(null)

		const {
			attachments,
			formattedAttachments,
			reset,
			resetComment,
			attachmentsHandlers,
			commentAttachments,
			commentAttachmentsHandlers,
			updateContent,
		} = useEditorState({})

		useSetBackNavigation({
			type: "thread-details",
			url: `/threads/${thread?.id}`,
		})

		// Global semicolon key handler to open snippet selector
		useEffect(() => {
			const handleGlobalKeydown = (event: KeyboardEvent) => {
				const activeElement = document.activeElement
				const isInputFocused =
					activeElement &&
					(activeElement.tagName === "INPUT" ||
						activeElement.tagName === "TEXTAREA" ||
						activeElement.tagName === "SELECT" ||
						activeElement.getAttribute("contenteditable") === "true")

				if (event.key === ";" && !isInputFocused) {
					event.preventDefault()
					if (composeEditor) {
						composeEditor.commands.focus("end", { scrollIntoView: false })
						setTimeout(() => {
							snippetSuggestionsRef.current?.openSnippets()
						}, 50)
					}
				}
			}

			document.addEventListener("keydown", handleGlobalKeydown)
			return () => {
				document.removeEventListener("keydown", handleGlobalKeydown)
			}
		}, [composeEditor])

		useEffect(() => {
			const editorDom = composeEditor?.view.dom
			if (!editorDom) return

			const handleOpenSnippets = () => {
				snippetSuggestionsRef.current?.openSnippets()
			}
			const handleOpenHelpArticles = () => {
				helpArticlesSuggestionsRef.current?.openHelpArticles()
			}

			editorDom.addEventListener("open-snippets", handleOpenSnippets)
			editorDom.addEventListener("open-help-articles", handleOpenHelpArticles)
			return () => {
				editorDom.removeEventListener("open-snippets", handleOpenSnippets)
				editorDom.removeEventListener(
					"open-help-articles",
					handleOpenHelpArticles,
				)
			}
		}, [composeEditor])

		const navigate = useNavigate()
		const workspace = useZeroWorkspace()
		const { session } = useSession()
		const [currentUser] = useQuery(queries.currentUser())
		const signerFirstName = draftSignerFirstName(
			currentUser?.name,
			session?.name,
		)
		const lockedBy = useThreadLockedBy(thread.id, session?.userId)
		const [lockDismissed, setLockDismissed] = useState(false)
		useEffect(() => {
			setLockDismissed(false)
		}, [thread.id])
		const showLockedComposer = !!lockedBy && !lockDismissed

		const isPhone = useIsPhone()
		const composerRootRef = useRef<HTMLDivElement>(null)
		const { collapsed } = useComposerCollapse({
			containerRef: composerRootRef,
			enabled: isPhone && !showLockedComposer,
			resetKey: thread.id,
		})
		const {
			addresses: fromAddresses,
			fromAddressId,
			fromAddress,
			setFromAddressId,
			showPicker: showFromPicker,
		} = useFromAddress()
		const [isForwardMode, setIsForwardMode] = useState(false)
		const [forwardRecipients, setForwardRecipients] = useState<EmailContact[]>(
			[],
		)
		const forwardToInputRef = useRef<HTMLInputElement>(null)
		const [isCcVisible, setIsCcVisible] = useState(false)
		const [ccRecipients, setCcRecipients] = useState<EmailContact[]>([])
		const [isBccVisible, setIsBccVisible] = useState(false)
		const [bccRecipients, setBccRecipients] = useState<EmailContact[]>([])
		const [isReplyAllMode, setIsReplyAllMode] = useState(false)
		// Resolved once per thread: either the reply-all default auto-applied or
		// the user acted first, so a later email hydration can't re-trigger it.
		const replyAllDefaultHandledRef = useRef(false)
		const focusComposeAfterCcBccCloseRef = useRef(false)

		const currentLiveChat = thread.liveChatSession

		const isWidgetLiveChatEnabled =
			!!workspace?.widgetSettings?.isLiveChatEnabled

		const replyType: ReplyType = useMemo(
			() =>
				replyTypeFor(
					{ origin: thread.origin, liveChatSession: currentLiveChat },
					isWidgetLiveChatEnabled,
				),
			[thread.origin, currentLiveChat, isWidgetLiveChatEnabled],
		)

		const {
			handleEmailContentPopulation,
			buildEmailHistoryHtml,
			hasClickedReply,
			setHasClickedReply,
			replyAllByDefault,
		} = useEmailContentPopulation({
			composeEditor,
			replyType,
			isForwardMode,
		})

		const zero = useZero()

		// Composer source state — drives the auto-seed gate and the badge.
		const composerEntry = useComposerState((s) => s.entries[thread.id])
		const setComposerEntry = useComposerState((s) => s.setEntry)

		// Window guard around our own setContent: the editor's normalization
		// appends a follow-up transaction that fires `update`; ignore those.
		const seedingRef = useRef(false)
		const runProgrammaticEdit = useCallback((mutate: () => void) => {
			seedingRef.current = true
			mutate()
			requestAnimationFrame(() => {
				seedingRef.current = false
			})
		}, [])

		// `started > generated` means a run is in flight.
		const aiDraftStartedAt = thread.aiDraftStartedAt ?? 0
		const aiDraftGeneratedAt = thread.aiDraftGeneratedAt ?? 0
		const isAiDraftGenerating = aiDraftStartedAt > aiDraftGeneratedAt

		// While a draft exists, it's always reachable — either as the
		// "Drafted by AI" label (source still "ai") or the "Use AI draft"
		// button (agent has typed over it but can re-apply). The draft is
		// only cleared on send or when a new inbound message arrives.
		const staleTick = useAiDraftStaleTick(
			isAiDraftGenerating ? aiDraftStartedAt : null,
		)
		const rawAiDraftBadgeState = resolveAiDraftBadgeState({
			startedAt: aiDraftStartedAt,
			generatedAt: aiDraftGeneratedAt,
			errorAt: thread.aiDraftErrorAt ?? 0,
			hasDraftHtml: !!thread.aiDraftHtml,
			composerSource: composerEntry?.source ?? null,
			appliedAiDraftAt: composerEntry?.appliedAiDraftAt ?? null,
			now: staleTick,
		})

		// A failed draft on a workspace that has spent its AI budget gets the
		// budget pill instead, which carries the fix.
		const { data: aiBudget } = api.workspace.getAiBudgetStatus.useQuery(
			undefined,
			{ enabled: rawAiDraftBadgeState === "error", staleTime: 60_000 },
		)
		const aiDraftBadgeState =
			rawAiDraftBadgeState === "error" && aiBudget?.blocked
				? ("budget-reached" as const)
				: rawAiDraftBadgeState

		// Auto-seed the composer with the AI draft. Keyed by
		// `${threadId}:${generatedAt}` so thread switches and regenerations
		// both get a chance to apply. Skip when the composer already has
		// content the user might want preserved.
		const lastSeededDraftKeyRef = useRef<string | null>(null)
		useEffect(() => {
			if (!composeEditor) return
			const draft = thread.aiDraftHtml
			const generatedAt = thread.aiDraftGeneratedAt ?? null
			if (!draft || !generatedAt) return
			const key = `${thread.id}:${generatedAt}`
			if (lastSeededDraftKeyRef.current === key) return

			// Only a genuine user-authored body blocks the seed; the email scaffold
			// and an untouched AI-seeded body (source still "ai") don't, so a
			// regenerated draft replaces the stale one it superseded.
			if (
				hasUserAuthoredBody(composerEntry?.content) &&
				composerEntry?.source !== "ai"
			) {
				return
			}

			// Seed the draft body with the quoted thread history below it, so an
			// AI-drafted reply carries the same conversation history a manual reply
			// would auto-quote.
			const bodyHTML = `${renderAiDraftAsHtml(
				draft,
				signerFirstName,
			)}${buildEmailHistoryHtml()}`
			const rafId = requestAnimationFrame(() => {
				// Burn the key only once the seed actually runs; a cancelled frame
				// (deps re-run before the rAF fires) must leave it free to retry.
				lastSeededDraftKeyRef.current = key
				runProgrammaticEdit(() =>
					composeEditor.chain().setContent(bodyHTML, false).run(),
				)
				// Store normalized getHTML(), not raw bodyHTML, so center-content's
				// hydration sees content === getHTML() and skips a re-write.
				setComposerEntry(thread.id, {
					content: composeEditor.getHTML(),
					source: "ai",
					appliedAiDraftAt: generatedAt,
				})
				setHasClickedReply(true)
				setIsReply(true)
			})
			return () => cancelAnimationFrame(rafId)
		}, [
			composeEditor,
			thread.id,
			thread.aiDraftHtml,
			thread.aiDraftGeneratedAt,
			composerEntry,
			setComposerEntry,
			signerFirstName,
			setHasClickedReply,
			setIsReply,
			runProgrammaticEdit,
			buildEmailHistoryHtml,
		])

		// Flip to "user" on the first genuine edit. `seedingRef` and the
		// isEmpty check filter out programmatic writes and empty-doc churn.
		const userEditFiredRef = useRef(false)
		useEffect(() => {
			if (!composeEditor) return
			userEditFiredRef.current = false
			const handler = () => {
				if (seedingRef.current) return
				if (userEditFiredRef.current) return
				// Empty-doc churn (editor (re)mount / hydration clearing) fires
				// updates that aren't user edits. A real reply edit is never empty.
				if (composeEditor.isEmpty) return
				userEditFiredRef.current = true
				if (composerEntry?.source !== "user") {
					setComposerEntry(thread.id, { source: "user" })
				}
			}
			composeEditor.on("update", handler)
			return () => {
				composeEditor.off("update", handler)
			}
		}, [composeEditor, thread.id, composerEntry?.source, setComposerEntry])

		// Click-to-apply on the AI draft pill. The agent has explicitly
		// chosen to use the draft, so we consume it: composer fills with
		// the draft text and `cancelAiDraft` wipes the persisted draft so
		// the badge is gone. Undo reverts the composer body but does not
		// restore the persisted draft — once consumed, it stays consumed.
		const onApplyAiDraft = useCallback(() => {
			if (!composeEditor || !thread.aiDraftHtml) return
			const generatedAt = thread.aiDraftGeneratedAt ?? 0
			const before = {
				content: composeEditor.getHTML(),
				source: composerEntry?.source ?? null,
				appliedAiDraftAt: composerEntry?.appliedAiDraftAt ?? null,
			}
			// If the composer was effectively empty before the click, there's
			// nothing meaningful to undo *to* — skip the toast entirely.
			const hadMeaningfulContent = !isEmptyContentTag(before.content)
			const bodyHTML = `${renderAiDraftAsHtml(
				thread.aiDraftHtml,
				signerFirstName,
			)}${buildEmailHistoryHtml()}`
			runProgrammaticEdit(() =>
				composeEditor
					.chain()
					.setContent(bodyHTML, false)
					.focus("end", { scrollIntoView: false })
					.run(),
			)
			setComposerEntry(thread.id, {
				content: composeEditor.getHTML(),
				source: "ai",
				appliedAiDraftAt: generatedAt,
			})
			lastSeededDraftKeyRef.current = `${thread.id}:${generatedAt}`
			setHasClickedReply(true)
			setIsReply(true)
			void zero.mutate(mutators.threads.cancelAiDraft({ threadId: thread.id }))
			if (!hadMeaningfulContent) return
			toast.success({
				title: "Replaced with AI draft",
				time: 5000,
				action: {
					label: "Undo",
					callback: () => {
						runProgrammaticEdit(() =>
							composeEditor
								.chain()
								.setContent(before.content, false)
								.focus("end", { scrollIntoView: false })
								.run(),
						)
						setComposerEntry(thread.id, {
							content: before.content,
							source: before.source,
							appliedAiDraftAt: before.appliedAiDraftAt,
						})
					},
				},
			})
		}, [
			composeEditor,
			thread.id,
			thread.aiDraftHtml,
			thread.aiDraftGeneratedAt,
			composerEntry?.source,
			composerEntry?.appliedAiDraftAt,
			setComposerEntry,
			signerFirstName,
			setHasClickedReply,
			setIsReply,
			zero,
			runProgrammaticEdit,
			buildEmailHistoryHtml,
		])

		// Delete the draft. If it's in the composer (source "ai"), clear the body
		// and offer undo; otherwise just discard the persisted draft.
		const onDeleteAiDraft = useCallback(() => {
			if (composerEntry?.source !== "ai" || !composeEditor) {
				void zero.mutate(
					mutators.threads.cancelAiDraft({ threadId: thread.id }),
				)
				return
			}
			const before = {
				content: composeEditor.getHTML(),
				source: composerEntry?.source ?? null,
				appliedAiDraftAt: composerEntry?.appliedAiDraftAt ?? null,
			}
			runProgrammaticEdit(() =>
				composeEditor
					.chain()
					.clearContent()
					.focus("end", { scrollIntoView: false })
					.run(),
			)
			setComposerEntry(thread.id, {
				content: "",
				source: "user",
				appliedAiDraftAt: null,
			})
			void zero.mutate(mutators.threads.cancelAiDraft({ threadId: thread.id }))
			toast.success({
				title: "Deleted AI draft",
				time: 5000,
				action: {
					label: "Undo",
					callback: () => {
						runProgrammaticEdit(() =>
							composeEditor
								.chain()
								.setContent(before.content, false)
								.focus("end", { scrollIntoView: false })
								.run(),
						)
						setComposerEntry(thread.id, {
							content: before.content,
							source: before.source,
							appliedAiDraftAt: before.appliedAiDraftAt,
						})
					},
				},
			})
		}, [
			composeEditor,
			thread.id,
			composerEntry?.source,
			composerEntry?.appliedAiDraftAt,
			setComposerEntry,
			zero,
			runProgrammaticEdit,
		])

		const onRegenerateAiDraft = useCallback(() => {
			void zero.mutate(
				mutators.threads.regenerateAiDraft({
					threadId: thread.id,
					startedAtIso: new Date().toISOString(),
				}),
			)
		}, [zero, thread.id])

		const onAdjustAiDraft = useCallback(
			(instruction: string) => {
				void zero.mutate(
					mutators.threads.regenerateAiDraft({
						threadId: thread.id,
						startedAtIso: new Date().toISOString(),
						instruction,
					}),
				)
			},
			[zero, thread.id],
		)

		const onOpenAiSettings = useCallback(() => {
			void navigate({ to: "/settings/ai/usage" })
		}, [navigate])

		const { requestPermission, permission } = useBrowserNotifications()

		const handleLiveChatFocus = useCallback(() => {
			if (replyType === "live-chat" && permission === "default") {
				void requestPermission()
			}
		}, [replyType, permission, requestPermission])

		const switchTab = useCallback(
			(tab: TabType) => {
				if (tab === "comment") {
					if (!commentEditor) onActivateCommentEditor?.()
					setPendingCommentFocus(true)
				}
				if (tab === activeTab) return
				// Hold the current height across the switch so the box doesn't jump
				// when each tab's chrome auto-sizes differently.
				const currentHeight = editorContainerRef.current?.offsetHeight
				if (currentHeight && !collapsed) {
					setBarHeight((h) => h ?? currentHeight)
				}
				setActiveTab(tab)
				if (tab === "reply") {
					setIsReply(true)
					setIsComment(false)
				} else {
					setIsComment(true)
					setIsReply(false)
					setIsForwardMode(false)
				}
			},
			[
				activeTab,
				collapsed,
				setIsReply,
				setIsComment,
				commentEditor,
				onActivateCommentEditor,
			],
		)

		const typingEmitter = useTypingEmitter((isTyping) => {
			if (!liveChatSocket?.connected || !currentLiveChat?.id) return
			liveChatSocket.emit("live-chat:typing", {
				sessionId: currentLiveChat.id,
				isTyping,
			})
		})

		const teamTypingEmitter = useTypingEmitter(
			(isTyping) => updateDocState(`thread:${thread.id}`, { typing: isTyping }),
			{ idleMs: TEAM_TYPING_IDLE_MS },
		)

		const handleTyping = () => {
			teamTypingEmitter.notifyTyping()
			if (replyType !== "live-chat") return
			if (!liveChatSocket?.connected || !currentLiveChat?.id) return
			typingEmitter.notifyTyping()
		}

		// Reply defaults to the contact only; Cc/Bcc start empty and collapsed so
		// the previous email's distribution list never auto-lands on an outbound
		// reply and no open panel carries over from another thread. The agent
		// opts into the original recipients via "Reply all".
		useEffect(() => {
			setCcRecipients([])
			setBccRecipients([])
			setIsCcVisible(false)
			setIsBccVisible(false)
			setIsReplyAllMode(false)
			replyAllDefaultHandledRef.current = false
		}, [thread.id])

		const replyAllEmails = useMemo(
			() =>
				(thread.emails ?? [])
					.filter((e) => e.threadId === thread.id && e.source !== "AUTO_REPLY")
					.sort((a, b) => b.createdAt - a.createdAt)
					.map((e) => ({
						type:
							e.type === "INBOUND"
								? ("INBOUND" as const)
								: ("OUTBOUND" as const),
						from: formatEmailContacts(e.from)[0] ?? null,
						to: formatEmailContacts(e.to),
						cc: formatEmailContacts(e.cc),
						bcc: formatEmailContacts(e.bcc),
						originalRecipient: e.originalRecipient,
					})),
			[thread.emails, thread.id],
		)

		// Every address the workspace owns counts as "us", not just the primary.
		const workspaceSelfEmails = useMemo(
			() =>
				(workspace?.emailSettings ?? []).flatMap((address) => [
					address.primaryEmail,
					address.brandedInboundEmailAddress,
				]),
			[workspace?.emailSettings],
		)

		const computeReplyAllRecipients = useCallback(
			(): { cc: EmailContact[]; bcc: EmailContact[] } =>
				computeReplyAllRecipientsFor({
					emailsNewestFirst: replyAllEmails,
					selfEmails: [session?.email, ...workspaceSelfEmails],
					contactEmail: thread.contact?.email,
				}),
			[
				replyAllEmails,
				thread.contact?.email,
				session?.email,
				workspaceSelfEmails,
			],
		)

		const hasReplyAllRecipients = useMemo(() => {
			const { cc, bcc } = computeReplyAllRecipients()
			return cc.length > 0 || bcc.length > 0
		}, [computeReplyAllRecipients])

		// Applies reply-all: switch to reply, enable reply-all mode, and populate
		// Cc/Bcc from the original recipients. `revealFields` controls whether the
		// Cc/Bcc inputs expand. Manual reply-all opens them; the "reply all by
		// default" preference applies reply-all with the panel collapsed (the
		// recipients still send and the header shows the Cc/Bcc count).
		const applyReplyAll = useCallback(
			({ revealFields }: { revealFields: boolean }) => {
				if (activeTab !== "reply") switchTab("reply")
				if (isForwardMode) setIsForwardMode(false)
				setIsReplyAllMode(true)
				const { cc, bcc } = computeReplyAllRecipients()
				setCcRecipients(cc)
				setBccRecipients(bcc)
				if (revealFields) {
					setIsCcVisible(true)
					setIsBccVisible(true)
				}
				if (replyType === "email" && !hasClickedReply) {
					handleEmailContentPopulation()
					setHasClickedReply(true)
				}
			},
			[
				activeTab,
				switchTab,
				isForwardMode,
				computeReplyAllRecipients,
				replyType,
				hasClickedReply,
				handleEmailContentPopulation,
				setHasClickedReply,
			],
		)

		const handleReplyAll = useCallback(() => {
			applyReplyAll({ revealFields: true })
		}, [applyReplyAll])

		// Plain Reply is contact-only, like Gmail: leaving reply-all drops all
		// Cc/Bcc regardless of any edits.
		const handleSelectPlainReply = useCallback(() => {
			setIsReplyAllMode(false)
			setCcRecipients([])
			setBccRecipients([])
			replyAllDefaultHandledRef.current = true
		}, [])

		// Honor the user's "Reply all by default" preference: when an email
		// thread has other recipients, open straight into reply-all instead of
		// plain reply. `hasReplyAllRecipients` is false until emails hydrate, so
		// this fires on that transition, once per thread. The Cc/Bcc panel stays
		// collapsed here; it only expands when the agent selects reply-all by hand.
		useEffect(() => {
			if (!replyAllByDefault) return
			if (replyAllDefaultHandledRef.current) return
			if (replyType !== "email") return
			if (!hasReplyAllRecipients) return
			if (isForwardMode) return
			if (activeTab !== "reply") return
			if (isReplyAllMode) return
			replyAllDefaultHandledRef.current = true
			applyReplyAll({ revealFields: false })
		}, [
			replyAllByDefault,
			replyType,
			hasReplyAllRecipients,
			isForwardMode,
			activeTab,
			isReplyAllMode,
			applyReplyAll,
		])

		// Runs after every send. The ref resets too, so the next reply on the same
		// thread re-applies "reply all by default" instead of silently going out
		// contact-only.
		const clearReplyRecipients = useCallback(() => {
			setCcRecipients([])
			setBccRecipients([])
			setIsReplyAllMode(false)
			replyAllDefaultHandledRef.current = false
		}, [])

		useEffect(() => {
			if (isForwardMode) {
				setTimeout(() => {
					forwardToInputRef.current?.focus()
				}, 100)
			}
		}, [isForwardMode])

		useResetRecipientsOnForward(isForwardMode, clearReplyRecipients)

		const {
			send: sendMessage,
			pendingMessages,
			getMessageContent,
			getUndoMessageFn,
		} = useSendMessage({
			composeEditor,
			commentEditor,
			activeTab,
			replyType,
			isForwardMode,
			forwardRecipients,
			ccRecipients,
			bccRecipients,
			attachments,
			formattedAttachments,
			commentAttachments,
			reset,
			resetComment,
			updateContent,
			setIsReply,
			setIsComment,
			setHasClickedReply,
			setIsForwardMode,
			setForwardRecipients,
			clearReplyRecipients,
			fromAddressId,
		})

		// Sending drops the customer's "typing" view now instead of leaving it up
		// for the rest of the idle window.
		const send = useCallback(async () => {
			typingEmitter.stopTyping()
			teamTypingEmitter.stopTyping()
			await sendMessage()
		}, [sendMessage, typingEmitter, teamTypingEmitter])

		useBottomBarShortcuts({
			composeEditor,
			commentEditor,
			activeTab,
			replyType,
			hasClickedReply,
			send,
			switchTab,
			setIsForwardMode,
			setForwardRecipients,
			setHasClickedReply,
			handleEmailContentPopulation,
			onReplyAll: handleReplyAll,
			onSelectPlainReply: handleSelectPlainReply,
			pendingMessages,
			getMessageContent,
			getUndoMessageFn,
		})

		const {
			liveChatSocket,
			connectToLiveChat,
			setIsLiveChatOpen,
			unsubscribeFromSession,
		} = useLiveChatStore()

		useEffect(() => {
			if (replyType === "live-chat" && currentLiveChat?.id) {
				connectToLiveChat(currentLiveChat.id, {
					name: session?.name,
					imageUrl: session?.imageUrl || undefined,
					userId: session?.userId,
				})
				setIsLiveChatOpen(true)
			}
		}, [
			replyType,
			currentLiveChat?.id,
			connectToLiveChat,
			session?.name,
			session?.imageUrl,
			session?.userId,
			setIsLiveChatOpen,
		])

		// A widget thread replies by live chat before its session exists; without
		// leaving here the socket keeps the previous chat's room and its typer.
		useEffect(() => {
			if (replyType !== "live-chat" || !currentLiveChat?.id) {
				unsubscribeFromSession()
			}
		}, [replyType, currentLiveChat?.id, unsubscribeFromSession])

		const ref = useRef<HTMLDivElement | null>(null)

		const writeSnippet = useCallback(
			({ html }: SnippetInput) => {
				if (!composeEditor) return null
				insertSnippet(
					composeEditor,
					resolveMacroVariables(html, {
						contact: thread.contact,
						company: thread.company,
						user: { name: session?.name },
						revenueFormatting: workspace?.revenueFormatting,
						currency: workspace?.revenueCurrency,
					}),
				)
			},
			[
				composeEditor,
				thread.contact,
				thread.company,
				session?.name,
				workspace?.revenueFormatting,
				workspace?.revenueCurrency,
			],
		)

		const writeHelpArticle = useCallback(
			({ title, linkUrl }: { title: string; linkUrl: string }) => {
				if (!composeEditor) return null
				const { from } = composeEditor.state.selection
				const charBefore = composeEditor.state.doc.textBetween(
					Math.max(0, from - 1),
					from,
				)
				const chain = composeEditor.chain()
				if (charBefore === "@") {
					chain.deleteRange({ from: from - 1, to: from })
				}
				const linkHtml = `<a href="${linkUrl}" target="_blank" rel="noopener noreferrer">${title}</a>&nbsp;`
				chain
					.insertContent(linkHtml)
					.focus("end", { scrollIntoView: false })
					.run()
			},
			[composeEditor],
		)

		const threadSidebarState = useThreadSidebarState()

		const isSendButtonDisabled = useMemo(() => {
			if (session?.membershipRole === "VIEWER") return true
			if (composeEditorState?.isEmpty && !attachments.length) return true
			if (attachments.some((a) => a.state === "loading")) return true
			if (composeEditorState?.hasUnuploadedImage) return true
			return false
		}, [
			attachments,
			composeEditorState?.isEmpty,
			composeEditorState?.hasUnuploadedImage,
			session?.membershipRole,
		])

		const handleReplyDisabledClick = useCallback(() => {
			if (replyType === "disabled") {
				toast.error({
					title: "Reply not available",
					description: "Replies are not supported for this thread type.",
				})
			} else if (isForwardMode && forwardRecipients.length === 0) {
				toast.info({
					title: "Recipient required",
					description: "Please add at least one recipient to forward to.",
				})
			} else if (replyType === "email" && !thread.contactId) {
				toast.error({
					title: "Contact required",
					description: "Please select a person to send an email to.",
				})
			} else if (replyType === "slack" && !workspace?.slackConnectSettings) {
				toast.error({
					title: "Slack not connected",
					description: "Please connect Slack to your workspace.",
					action: {
						label: "Configure Slack integration",
						callback: () => {
							void navigate({ to: "/settings/integrations/slack" })
						},
					},
				})
			} else if (replyType === "teams" && !workspace?.teamsSettings) {
				toast.error({
					title: "Teams not connected",
					description: "Please connect Teams to your workspace.",
					action: {
						label: "Configure Teams integration",
						callback: () => {
							void navigate({ to: "/settings/integrations/teams" })
						},
					},
				})
			} else if (composeEditor?.isEmpty && !attachments.length) {
				toast.error({
					title: "Message required",
					description: "Please add a message before sending.",
				})
			} else if (attachments.some((a) => a.state === "loading")) {
				toast.info({
					title: "Please wait for attachments to finish uploading.",
				})
			} else if (hasUnuploadedInlineImage(composeEditor?.state.doc)) {
				toast.info({
					title: "Image is still uploading",
					description: "Send once it finishes, or remove it to send now.",
					icon: IconImageFill18,
				})
			}
		}, [
			composeEditor,
			replyType,
			thread.contactId,
			workspace?.slackConnectSettings,
			workspace?.teamsSettings,
			attachments,
			isForwardMode,
			forwardRecipients.length,
			navigate,
		])

		const handleCommentDisabledClick = useCallback(() => {
			const isCommentDisabled =
				commentEditor?.isEmpty && !commentAttachments.length
			if (isCommentDisabled) {
				toast.error({
					title: "Comment required",
					description: "Please add a comment before submitting.",
				})
			}
		}, [commentEditor?.isEmpty, commentAttachments.length])

		const isCcBccPanelOpen = isCcVisible || isBccVisible
		useLayoutEffect(() => {
			if (!focusComposeAfterCcBccCloseRef.current) return
			focusComposeAfterCcBccCloseRef.current = false
			if (!composeEditor || composeEditor.isDestroyed) return
			composeEditor.commands.focus("start", { scrollIntoView: false })
		}, [composeEditor, isCcVisible, isBccVisible])

		const toggleCcBccPanel = useCallback(() => {
			if (isCcBccPanelOpen) {
				focusComposeAfterCcBccCloseRef.current = true
				setIsCcVisible(false)
				setIsBccVisible(false)
			} else {
				setIsCcVisible(true)
				setIsBccVisible(true)
				setTimeout(() => {
					ccInputRef.current?.focus()
				}, 100)
			}
		}, [isCcBccPanelOpen])

		const showEmailRecipientRow =
			composeEditorState?.isFocused || isCcBccPanelOpen

		const [barHeight, setBarHeight] = useState<number | null>(() => {
			if (typeof window === "undefined") return null
			const stored = window.localStorage.getItem(BAR_HEIGHT_STORAGE_KEY)
			const parsed = stored ? parseInt(stored, 10) : NaN
			return Number.isFinite(parsed) ? parsed : null
		})
		const scrollAreaRef = useRef<HTMLDivElement | null>(null)

		const [barMaxHeight, setBarMaxHeight] = useState(getBarMaxHeight)
		useEffect(() => {
			const onResize = () => setBarMaxHeight(getBarMaxHeight())
			window.addEventListener("resize", onResize)
			window.visualViewport?.addEventListener("resize", onResize)
			return () => {
				window.removeEventListener("resize", onResize)
				window.visualViewport?.removeEventListener("resize", onResize)
			}
		}, [])

		const handleResizeStart = useCallback(
			(e: React.PointerEvent<HTMLDivElement>) => {
				const container = editorContainerRef.current
				if (!container) return
				e.preventDefault()
				const handle = e.currentTarget
				const { pointerId, clientY: startY } = e
				// Capture keeps the drag alive when a finger slides off the thin handle.
				handle.setPointerCapture?.(pointerId)
				const startHeight = container.offsetHeight || BAR_MIN_HEIGHT
				let height = startHeight
				let frame = 0
				// Pin once so the auto-size bounds drop off; the drag then writes the
				// height straight to the DOM instead of re-rendering the composer per move.
				setBarHeight(startHeight)

				const onMove = (moveEvent: PointerEvent) => {
					if (moveEvent.pointerId !== pointerId) return
					height = draggedBarHeight({
						startHeight,
						startY,
						clientY: moveEvent.clientY,
						maxHeight: barMaxHeight,
					})
					if (frame) return
					frame = requestAnimationFrame(() => {
						frame = 0
						container.style.height = `${height}px`
					})
				}
				const onEnd = (endEvent: PointerEvent) => {
					if (endEvent.pointerId !== pointerId) return
					cancelAnimationFrame(frame)
					handle.removeEventListener("pointermove", onMove)
					handle.removeEventListener("pointerup", onEnd)
					handle.removeEventListener("pointercancel", onEnd)
					document.body.style.userSelect = ""
					document.body.style.cursor = ""
					container.style.height = `${height}px`
					setBarHeight(height)
					window.localStorage.setItem(BAR_HEIGHT_STORAGE_KEY, String(height))
				}
				handle.addEventListener("pointermove", onMove)
				handle.addEventListener("pointerup", onEnd)
				handle.addEventListener("pointercancel", onEnd)
				document.body.style.userSelect = "none"
				document.body.style.cursor = "ns-resize"
			},
			[barMaxHeight],
		)

		// Autosize a manually-pinned box back up to fit content as the user
		// types, capped at the viewport limit. Only grows: a manual drag below
		// the content stays scrolled until new typing needs the room.
		const growToFitContent = useCallback(() => {
			setBarHeight((current) => {
				if (current == null) return current
				const el = scrollAreaRef.current
				if (!el) return current
				const overflow = el.scrollHeight - el.clientHeight
				if (overflow <= 0) return current
				const next = Math.min(barMaxHeight, current + overflow)
				if (next !== current) {
					window.localStorage.setItem(BAR_HEIGHT_STORAGE_KEY, String(next))
				}
				return next
			})
		}, [barMaxHeight])

		// Drop the manual height on send or when the editor is emptied, so the
		// box returns to its default auto-size. Dragging an empty box taller
		// still sticks because that doesn't fire an editor update.
		const resetBarHeight = useCallback(() => {
			setBarHeight((current) => {
				if (current == null) return current
				window.localStorage.removeItem(BAR_HEIGHT_STORAGE_KEY)
				return null
			})
		}, [])

		// A collapsed preview always overflows, so it must not feed the autosize.
		useEffect(() => {
			if (!composeEditor || collapsed) return
			const handler = () =>
				requestAnimationFrame(() => {
					if (activeTab !== "reply") return
					if (composeEditor.isEmpty) resetBarHeight()
					else growToFitContent()
				})
			composeEditor.on("update", handler)
			return () => {
				composeEditor.off("update", handler)
			}
		}, [composeEditor, activeTab, collapsed, growToFitContent, resetBarHeight])

		useEffect(() => {
			if (!commentEditor || collapsed) return
			const handler = () =>
				requestAnimationFrame(() => {
					if (activeTab !== "comment") return
					if (commentEditor.isEmpty) resetBarHeight()
					else growToFitContent()
				})
			commentEditor.on("update", handler)
			return () => {
				commentEditor.off("update", handler)
			}
		}, [commentEditor, activeTab, collapsed, growToFitContent, resetBarHeight])

		// The preview shows the start of the message, wherever the caret left it.
		useLayoutEffect(() => {
			if (collapsed && scrollAreaRef.current) {
				scrollAreaRef.current.scrollTop = 0
			}
		}, [collapsed])

		const isLocked = !!lockedBy && !lockDismissed
		useComposerDeadZoneFocus({
			containerRef: editorContainerRef,
			enabled: !isLocked,
			onDeadZonePress: () => {
				if (activeTab === "reply" && composeEditor) {
					if (replyType === "email" && !hasClickedReply) {
						handleEmailContentPopulation()
						setHasClickedReply(true)
					}
					composeEditor.commands.focus("end", { scrollIntoView: false })
				} else if (activeTab === "comment" && commentEditor) {
					commentEditor.commands.focus("end", { scrollIntoView: false })
				}
			},
		})

		if (isLocked) {
			return (
				<LockedComposer
					holder={lockedBy}
					onDismiss={() => setLockDismissed(true)}
				/>
			)
		}

		return (
			<div
				className={cn("group z-1 flex w-full justify-center", {
					"z-0": threadSidebarState.isOpen,
				})}
			>
				<div
					ref={composerRootRef}
					className={cn("mx-auto w-full max-w-[50rem]", THREAD_COLUMN_PADDING)}
				>
					{/* Positions the resize handle outside the box's overflow clip, so a
					    finger gets a target that straddles the top edge. */}
					<div className="relative -mx-2 mb-3 mt-[0.5px]">
						{!collapsed && (
							<div
								onPointerDown={handleResizeStart}
								className="group/resize absolute inset-x-0 top-0 z-20 flex h-2 cursor-ns-resize touch-none items-start justify-center max-sm:-top-2 max-sm:h-4"
								role="separator"
								aria-orientation="horizontal"
								aria-label="Resize reply box"
							>
								<div className="mt-px h-1 w-10 rounded-full bg-transparent transition-colors duration-150 group-hover/resize:bg-gray-300 max-sm:mt-2 dark:group-hover/resize:bg-gray-700" />
							</div>
						)}
						<div
							ref={editorContainerRef}
							className={cn(
								"relative flex w-auto cursor-text flex-col justify-center overflow-hidden rounded-2xl px-2 py-3.5 align-middle shadow-sm transition-colors duration-200 ease-out sm:px-3.5",
								barHeight == null &&
									!collapsed &&
									"max-h-[400px] min-h-[160px]",
								activeTab === "comment"
									? "border border-[#F2E6C9] bg-[#FFFBF2] dark:border-[#3D331C] dark:bg-[#221D12]"
									: cn(
											"border border-gray-950/15 bg-white dark:border-white/10 dark:shadow-black",
											INBOUND_SURFACE_DARK_BG,
										),
								activeTab === "reply" &&
									(composeEditorState?.isFocused ?? false) &&
									"border-gray-950/30 dark:border-white/25",
							)}
							style={
								collapsed
									? undefined
									: barHeight != null
										? { height: Math.min(barHeight, barMaxHeight) }
										: isPhone
											? { maxHeight: Math.min(400, barMaxHeight) }
											: undefined
							}
							onFocusCapture={() => {
								if (!commentEditor) {
									onActivateCommentEditor?.()
								}
							}}
						>
							<BottomBarHeader
								activeTab={activeTab}
								replyType={replyType}
								isForwardMode={isForwardMode}
								composeEditor={composeEditor}
								commentEditor={commentEditor}
								hasClickedReply={hasClickedReply}
								showEmailRecipientRow={showEmailRecipientRow}
								isCcBccPanelOpen={isCcBccPanelOpen}
								isReplyAllMode={isReplyAllMode}
								onReplyAll={handleReplyAll}
								onSelectPlainReply={handleSelectPlainReply}
								ccRecipients={ccRecipients}
								bccRecipients={bccRecipients}
								contactEmail={thread.contact?.email}
								aiDraftBadgeState={aiDraftBadgeState}
								aiDraftSources={thread.aiDraftSources ?? null}
								onApplyAiDraft={onApplyAiDraft}
								onDeleteAiDraft={onDeleteAiDraft}
								onRegenerateAiDraft={onRegenerateAiDraft}
								onAdjustAiDraft={onAdjustAiDraft}
								onOpenAiSettings={onOpenAiSettings}
								switchTab={switchTab}
								setIsForwardMode={setIsForwardMode}
								setForwardRecipients={setForwardRecipients}
								setHasClickedReply={setHasClickedReply}
								handleEmailContentPopulation={handleEmailContentPopulation}
								toggleCcBccPanel={toggleCcBccPanel}
								fromAddresses={fromAddresses}
								fromAddress={fromAddress}
								onSelectFromAddress={setFromAddressId}
								showFromPicker={showFromPicker}
								compact={isPhone}
							/>

							{/* Main content (text editor) */}
							<div
								ref={scrollAreaRef}
								className={cn(
									"flex-1 overflow-x-hidden overscroll-y-contain",
									collapsed
										? "max-h-[4.5rem] overflow-y-hidden [mask-image:linear-gradient(to_bottom,black_65%,transparent)]"
										: "overflow-y-auto",
									{
										"mt-4":
											activeTab === "comment" ||
											(activeTab === "reply" && replyType === "slack"),
									},
								)}
							>
								{activeTab === "reply" && thread.origin !== "SLACK_CONNECT" && (
									<div className="mt-4 flex flex-col gap-0">
										{isPhone &&
											!collapsed &&
											showFromPicker &&
											replyType === "email" && (
												<FromAddressPicker
													fromAddresses={fromAddresses}
													fromAddress={fromAddress}
													onSelectFromAddress={setFromAddressId}
													className="-ml-0.5 max-w-full self-start"
												/>
											)}
										{isForwardMode && !collapsed && (
											<div className="mt-2">
												<EmailInputList
													ref={forwardToInputRef}
													label="To"
													recipients={forwardRecipients}
													onRecipientsChange={setForwardRecipients}
												/>
											</div>
										)}
										<AnimatePresence>
											{isCcBccPanelOpen && !collapsed && (
												<motion.div
													initial={{ opacity: 0, height: 0 }}
													animate={{ opacity: 1, height: "auto" }}
													exit={{ opacity: 0, height: 0 }}
													transition={{ duration: 0.2, ease: "easeOut" }}
													style={{ overflow: "hidden" }}
													className="flex flex-col gap-0"
												>
													<div className="flex flex-col gap-0 pt-2">
														<EmailInputList
															ref={ccInputRef}
															label="Cc"
															recipients={ccRecipients}
															rowClassName="mb-0"
															onRecipientsChange={setCcRecipients}
														/>
														<EmailInputList
															ref={bccInputRef}
															label="Bcc"
															recipients={bccRecipients}
															rowClassName="mb-0"
															onRecipientsChange={setBccRecipients}
														/>
													</div>
												</motion.div>
											)}
										</AnimatePresence>
									</div>
								)}
								<div className="-mx-1 my-1 min-h-0">
									<>
										{composeEditor && (
											<ComposeEditorBubbleMenus
												composeEditor={composeEditor}
												activeTab={activeTab}
												addExistingAttachment={
													attachmentsHandlers.addExistingAttachment
												}
											/>
										)}
										{/* Always rendered to keep DOM stable; CSS hides when inactive */}
										<div
											className="editor-container overflow-x-hidden break-words"
											style={{
												display: activeTab === "reply" ? undefined : "none",
												wordBreak: "break-word",
												overflowWrap: "break-word",
											}}
											role="presentation"
											onClick={() => {
												if (
													replyType === "email" &&
													!hasClickedReply &&
													composeEditor
												) {
													handleEmailContentPopulation()
												}
												handleLiveChatFocus()
											}}
											onKeyDown={(e) => {
												if (
													e.key === " " &&
													replyType === "email" &&
													!hasClickedReply &&
													composeEditor
												) {
													handleEmailContentPopulation()
												}
											}}
										>
											{composeEditorVisible ? (
												<EditorContent
													className={cn(
														"overflow-x-hidden break-words [&_img]:!my-2",
														!collapsed && "min-h-[67.5px]",
													)}
													style={{
														wordBreak: "break-word",
														overflowWrap: "break-word",
													}}
													editor={composeEditor}
													onInput={handleTyping}
												/>
											) : (
												<div
													className={cn(
														"overflow-x-hidden break-words [&_img]:!my-2",
														!collapsed && "min-h-[67.5px]",
													)}
													aria-hidden
												/>
											)}
										</div>
										{commentEditor && (
											<BubbleMenu
												editor={commentEditor}
												tippyOptions={{
													getReferenceClientRect:
														liveSelectionRect(commentEditor),
													duration: 100,
													placement: "top",
													zIndex: 40,
													popperOptions: {
														modifiers: [
															{
																name: "customStyle",
																enabled: true,
																phase: "beforeWrite",
																fn({ state }) {
																	const tippyBox = state.elements.popper
																		.firstChild as HTMLElement | null
																	if (tippyBox) {
																		tippyBox.style.maxWidth = "none"
																	}
																},
															},
														],
													},
												}}
												shouldShow={({ editor }) =>
													activeTab === "comment" &&
													!!editor.state.selection &&
													!editor.state.selection.empty
												}
											>
												<div className="flex flex-col whitespace-nowrap rounded-lg border border-gray-200 bg-white p-1 font-medium text-gray-500 shadow-sm ring-1 ring-gray-300/30 dark:border-gray-800 dark:bg-gray-850 dark:text-gray-200 dark:ring-gray-700/40">
													<BubbleMenuFormatButtons
														editor={commentEditor}
														showLinkButtons={false}
													/>
												</div>
											</BubbleMenu>
										)}
										{/* Always rendered to keep DOM stable; CSS hides when inactive */}
										<EditorContent
											className={cn(
												"editor-container h-full overflow-x-hidden break-words [&_img]:!my-2",
												!collapsed && "min-h-[67.5px]",
											)}
											style={{
												display: activeTab === "comment" ? undefined : "none",
												wordBreak: "break-word",
												overflowWrap: "break-word",
											}}
											editor={commentEditor}
										/>
									</>
								</div>
							</div>

							{/* Bottom bar (send message, formatting, etc.) */}
							<div
								className={cn(
									"sticky bottom-0 z-10 -mx-4 px-4 transition-colors duration-200 ease-out",
									activeTab === "comment"
										? "bg-[#FFFBF2] dark:bg-[#221D12]"
										: cn("bg-white", INBOUND_SURFACE_DARK_BG),
									collapsed && "hidden",
								)}
							>
								{activeTab === "reply" && (
									<ReplyToolbar
										composeEditor={composeEditor}
										replyType={replyType}
										isForwardMode={isForwardMode}
										forwardRecipients={forwardRecipients}
										isSendButtonDisabled={isSendButtonDisabled}
										attachments={attachments}
										membershipRole={session?.membershipRole}
										containerRef={ref}
										snippetAnchorRef={editorContainerRef}
										snippetSuggestionsRef={snippetSuggestionsRef}
										writeSnippet={writeSnippet}
										helpArticlesAnchorRef={editorContainerRef}
										helpArticlesSuggestionsRef={helpArticlesSuggestionsRef}
										writeHelpArticle={writeHelpArticle}
										addAttachments={attachmentsHandlers.addAttachments}
										removeAttachment={attachmentsHandlers.removeAttachment}
										contactId={thread.contactId}
										slackConnectSettings={workspace?.slackConnectSettings}
										send={send}
										handleReplyDisabledClick={handleReplyDisabledClick}
									/>
								)}
								{activeTab === "comment" && (
									<CommentToolbar
										commentEditor={commentEditor}
										commentEditorIsEmpty={commentEditorState?.isEmpty ?? true}
										commentAttachments={commentAttachments}
										removeCommentAttachment={
											commentAttachmentsHandlers.removeAttachment
										}
										addCommentAttachments={
											commentAttachmentsHandlers.addAttachments
										}
										send={send}
										handleCommentDisabledClick={handleCommentDisabledClick}
									/>
								)}
							</div>
						</div>
					</div>
				</div>
			</div>
		)
	},
)

BottomBar.displayName = "BottomBar"
